
import { Component, OnInit } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import Swal from 'sweetalert2';
import { PointOfSaleService } from '../../../../../services/pos';

type DayKey =
  | 'sunday'
  | 'monday'
  | 'tuesday'
  | 'wednesday'
  | 'thursday'
  | 'friday'
  | 'saturday';

interface OpeningShift {
  start: string;
  end: string;
}

interface DaySchedule {
  day: DayKey;
  enabled: boolean;
  shifts: OpeningShift[];
}

interface BackendOpeningHour {
  id?: number;
  warehouse_id?: number;
  day: DayKey;
  is_open: boolean | number | string;
  opens_at?: string | null;
  closes_at?: string | null;
  shift_order?: number;
  shifts?: OpeningShift[] | string | null;
  start?: string | null;
  end?: string | null;
}

@Component({
  selector: 'app-clinic-appointments-mobile',
  templateUrl: './clinic-appointments-mobile.component.html',
  styleUrl: './clinic-appointments-mobile.component.scss'
})
export class ClinicAppointmentsMobileComponent implements OnInit {
  warehouseId!: string | number;
  isAr = localStorage.getItem('language') === 'ar';
  readonly hourOptions = Array.from({ length: 12 }, (_, index) => index + 1);
  readonly minuteOptions = Array.from({ length: 60 }, (_, index) => index.toString().padStart(2, '0'));

  selectedBusinessName = 'Business';

  contentReady = true;
  saving = false;
  submitted = false;
  serverError = '';

  hasSavedSchedule = false;
  viewMode: 'form' | 'table' = 'form';

  savedSchedule: DaySchedule[] = [];
  draftSchedule: DaySchedule[] = this.createDefaultSchedule();

  private readonly daysOrder: DayKey[] = [
    'sunday',
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday'
  ];

  constructor(
    private posService: PointOfSaleService,
    private transloco: TranslocoService
  ) {}

  ngOnInit(): void {
    const posId = this.getPosId();

    if (!posId) {
      this.contentReady = false;
      this.serverError = this.t('messages.noSelectedBusiness');
      this.viewMode = 'form';
      this.hasSavedSchedule = false;
      this.draftSchedule = this.createDefaultSchedule();
      return;
    }

    this.warehouseId = posId;
    this.getOpeningHours();
  }

  getOpeningHours(): void {
    this.contentReady = true;
    this.serverError = '';

    this.posService.getOpeningHours(this.warehouseId).subscribe({
      next: (res: any) => {
        const apiSchedule = this.extractScheduleFromResponse(res);

        this.selectedBusinessName =
          res?.warehouse_name ||
          res?.warehouse?.name ||
          res?.warehouse?.name_en ||
          this.selectedBusinessName;

        const normalizedSchedule = this.normalizeSchedule(apiSchedule);
        const hasRealSavedTimes = this.hasAnyRealSavedTime(normalizedSchedule);

        if (apiSchedule.length > 0 && hasRealSavedTimes) {
          this.savedSchedule = normalizedSchedule;
          this.draftSchedule = this.cloneSchedule(this.savedSchedule);
          this.hasSavedSchedule = true;
          this.viewMode = 'table';
        } else {
          this.savedSchedule = [];
          this.draftSchedule = this.createDefaultSchedule();
          this.hasSavedSchedule = false;
          this.viewMode = 'form';
        }

        this.contentReady = false;
      },
      error: (error) => {
        this.contentReady = false;
        this.hasSavedSchedule = false;
        this.viewMode = 'form';
        this.draftSchedule = this.createDefaultSchedule();

        this.serverError =
          error?.error?.message ||
          this.t('messages.loadFailed');
      }
    });
  }

  openFormForEdit(): void {
    this.submitted = false;
    this.serverError = '';
    this.draftSchedule = this.cloneSchedule(this.savedSchedule);
    this.viewMode = 'form';
  }

  cancelEdit(): void {
    this.submitted = false;
    this.serverError = '';

    if (this.hasSavedSchedule) {
      this.draftSchedule = this.cloneSchedule(this.savedSchedule);
      this.viewMode = 'table';
      return;
    }

    this.draftSchedule = this.createDefaultSchedule();
    this.viewMode = 'form';
  }

  toggleDay(day: DaySchedule): void {
    day.enabled = !day.enabled;

    if (day.enabled && day.shifts.length === 0) {
      day.shifts = [
        {
          start: '10:00',
          end: '22:00'
        }
      ];
    }

    if (!day.enabled) {
      day.shifts = [];
    }
  }

  addShift(day: DaySchedule): void {
    if (!day.enabled) {
      day.enabled = true;
    }

    day.shifts.push({
      start: '10:00',
      end: '22:00'
    });
  }

  removeShift(day: DaySchedule, shiftIndex: number): void {
    day.shifts.splice(shiftIndex, 1);

    if (day.shifts.length === 0) {
      day.enabled = false;
    }
  }

  updateShift(
    day: DaySchedule,
    shiftIndex: number,
    field: keyof OpeningShift,
    event: Event
  ): void {
    const value = (event.target as HTMLInputElement).value;
    day.shifts[shiftIndex][field] = value;
  }

  updateArabicTimePart(
    day: DaySchedule,
    shiftIndex: number,
    field: keyof OpeningShift,
    part: 'hour' | 'minute' | 'period',
    event: Event
  ): void {
    const value = (event.target as HTMLSelectElement).value;
    const currentTime = day.shifts[shiftIndex][field] || '10:00';
    const [hourText, minuteText] = currentTime.split(':');
    const currentHour = Number(hourText);
    const currentMinute = minuteText || '00';

    let hour12 = this.getTimeHour12(currentTime);
    let minute = currentMinute;
    let period = currentHour < 12 ? 'am' : 'pm';

    if (part === 'hour') {
      hour12 = Number(value);
    }

    if (part === 'minute') {
      minute = value.padStart(2, '0');
    }

    if (part === 'period') {
      period = value;
    }

    day.shifts[shiftIndex][field] = `${this.to24Hour(hour12, period)}:${minute}`;
  }

  onSubmit(): void {
    this.submitted = true;
    this.serverError = '';

    if (!this.isScheduleValid() || this.saving) {
      return;
    }

    const payload = {
      schedule: this.draftSchedule.map((item) => ({
        day: item.day,
        is_open: item.enabled,
        shifts: item.enabled ? item.shifts : []
      }))
    };

    this.saving = true;

    this.posService.setOpeningSchedule(this.warehouseId, payload).subscribe({
      next: (res: any) => {
        const apiSchedule = this.extractScheduleFromResponse(res);

        if (apiSchedule.length > 0) {
          const normalized = this.normalizeSchedule(apiSchedule);

          this.savedSchedule = this.hasAnyRealSavedTime(normalized)
            ? normalized
            : this.cloneSchedule(this.draftSchedule);
        } else {
          this.savedSchedule = this.cloneSchedule(this.draftSchedule);
        }

        this.draftSchedule = this.cloneSchedule(this.savedSchedule);
        this.hasSavedSchedule = true;
        this.viewMode = 'table';
        this.submitted = false;
        this.saving = false;

        Swal.fire({
          icon: 'success',
          title: this.t('messages.savedTitle'),
          text: this.t('messages.saveSuccess'),
          timer: 1400,
          showConfirmButton: false
        });
      },
      error: (error) => {
        this.saving = false;

        this.serverError =
          error?.error?.message ||
          this.t('messages.saveFailed');

        Swal.fire({
          icon: 'error',
          title: this.t('messages.errorTitle'),
          text: this.serverError
        });
      }
    });
  }

  isScheduleValid(): boolean {
    return this.draftSchedule.every((day) => {
      if (!day.enabled) return true;

      if (!day.shifts.length) return false;

      return day.shifts.every((shift) => {
        return Boolean(shift.start && shift.end && shift.start !== shift.end);
      });
    });
  }

  hasDayError(day: DaySchedule): boolean {
    if (!this.submitted || !day.enabled) return false;

    if (!day.shifts.length) return true;

    return day.shifts.some((shift) => {
      return !shift.start || !shift.end || shift.start === shift.end;
    });
  }

  getScheduleText(day: DaySchedule): string {
    if (!day.enabled || !day.shifts.length) {
      return '';
    }

    return day.shifts
      .map((shift) => this.formatShiftText(shift))
      .join(' / ');
  }

  formatShiftText(shift: OpeningShift): string {
    return `${this.formatTime(shift.start)} - ${this.formatTime(shift.end)}`;
  }

  formatTime(time: string): string {
    if (!time) return '';

    const [hourText, minuteText] = time.split(':');
    const hour = Number(hourText);
    const minute = Number(minuteText);

    if (Number.isNaN(hour) || Number.isNaN(minute)) {
      return time;
    }

    const period = hour < 12 ? 'morning' : 'evening';
    const hour12 = hour % 12 || 12;
    const formattedTime = `${hour12.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;

    if (!this.isAr) {
      return time;
    }

    return `${this.toArabicDigits(formattedTime)} ${period === 'morning' ? 'صباحًا' : 'مساءً'}`;
  }

  getTimeHour12(time: string): number {
    const hour = Number((time || '10:00').split(':')[0]);

    if (Number.isNaN(hour)) {
      return 10;
    }

    return hour % 12 || 12;
  }

  getTimeMinute(time: string): string {
    const minute = (time || '10:00').split(':')[1] || '00';
    return minute.padStart(2, '0');
  }

  getTimePeriod(time: string): 'am' | 'pm' {
    const hour = Number((time || '10:00').split(':')[0]);
    return !Number.isNaN(hour) && hour >= 12 ? 'pm' : 'am';
  }

  getTimeOptionLabel(value: string | number): string {
    return this.isAr ? this.toArabicDigits(value) : String(value);
  }

  getPeriodOptionLabel(period: 'am' | 'pm'): string {
    if (!this.isAr) {
      return period.toUpperCase();
    }

    return period === 'am' ? 'صباحًا' : 'مساءً';
  }

  toArabicDigits(value: string | number): string {
    return String(value).replace(/\d/g, (digit) => '٠١٢٣٤٥٦٧٨٩'[Number(digit)]);
  }

  isOvernightShift(shift: OpeningShift): boolean {
    if (!shift.start || !shift.end) return false;
    return shift.end < shift.start;
  }

  trackByDay(index: number, item: DaySchedule): DayKey {
    return item.day;
  }

  trackByShift(index: number): number {
    return index;
  }

  private t(key: string): string {
    return this.transloco.translate(`clinicAppointments.${key}`);
  }

  private getPosId(): string | null {
    try {
      return localStorage.getItem('posId');
    } catch {
      return null;
    }
  }

  private extractScheduleFromResponse(res: any): BackendOpeningHour[] {
    if (Array.isArray(res?.data)) {
      return res.data;
    }

    if (Array.isArray(res?.schedule)) {
      return res.schedule;
    }

    if (Array.isArray(res?.opening_hours)) {
      return res.opening_hours;
    }

    if (Array.isArray(res)) {
      return res;
    }

    return [];
  }

  private normalizeSchedule(apiSchedule: BackendOpeningHour[]): DaySchedule[] {
    const scheduleMap = new Map<DayKey, DaySchedule>();

    this.daysOrder.forEach((day) => {
      const dayRows = apiSchedule
        .filter((item) => item.day === day)
        .sort((a, b) => Number(a.shift_order || 0) - Number(b.shift_order || 0));

      if (!dayRows.length) {
        scheduleMap.set(day, {
          day,
          enabled: false,
          shifts: []
        });

        return;
      }

      const isOpen = this.toBoolean(dayRows[0].is_open);

      const shifts = dayRows
        .flatMap((row) => this.extractShiftsFromRow(row))
        .filter((shift) => shift.start && shift.end);

      scheduleMap.set(day, {
        day,
        enabled: isOpen,
        shifts: isOpen ? shifts : []
      });
    });

    return this.daysOrder.map((day) => {
      return (
        scheduleMap.get(day) || {
          day,
          enabled: false,
          shifts: []
        }
      );
    });
  }

  private extractShiftsFromRow(row: BackendOpeningHour): OpeningShift[] {
    const shiftsFromJson = this.parseShifts(row.shifts);

    if (shiftsFromJson.length > 0) {
      return shiftsFromJson;
    }

    if (row.opens_at && row.closes_at) {
      return [
        {
          start: this.normalizeTime(row.opens_at),
          end: this.normalizeTime(row.closes_at)
        }
      ];
    }

    if (row.start && row.end) {
      return [
        {
          start: this.normalizeTime(row.start),
          end: this.normalizeTime(row.end)
        }
      ];
    }

    return [];
  }

  private parseShifts(shifts: OpeningShift[] | string | null | undefined): OpeningShift[] {
    if (!shifts) return [];

    if (Array.isArray(shifts)) {
      return shifts
        .map((shift) => ({
          start: this.normalizeTime(shift.start),
          end: this.normalizeTime(shift.end)
        }))
        .filter((shift) => shift.start && shift.end);
    }

    try {
      const parsed = JSON.parse(shifts);

      if (!Array.isArray(parsed)) {
        return [];
      }

      return parsed
        .map((shift) => ({
          start: this.normalizeTime(shift.start),
          end: this.normalizeTime(shift.end)
        }))
        .filter((shift) => shift.start && shift.end);
    } catch {
      return [];
    }
  }

  private hasAnyRealSavedTime(schedule: DaySchedule[]): boolean {
    return schedule.some((day) => {
      return day.enabled && day.shifts.some((shift) => shift.start && shift.end);
    });
  }

  private toBoolean(value: boolean | number | string): boolean {
    return value === true || value === 1 || value === '1' || value === 'true';
  }

  private normalizeTime(time: string): string {
    if (!time) return '';
    return time.substring(0, 5);
  }

  private to24Hour(hour12: number, period: string): string {
    const normalizedHour = period === 'pm'
      ? (hour12 % 12) + 12
      : hour12 % 12;

    return normalizedHour.toString().padStart(2, '0');
  }

  private createDefaultSchedule(): DaySchedule[] {
    return [
      {
        day: 'sunday',
        enabled: true,
        shifts: [{ start: '10:00', end: '22:00' }]
      },
      {
        day: 'monday',
        enabled: true,
        shifts: [{ start: '10:00', end: '22:00' }]
      },
      {
        day: 'tuesday',
        enabled: true,
        shifts: [{ start: '10:00', end: '22:00' }]
      },
      {
        day: 'wednesday',
        enabled: true,
        shifts: [{ start: '10:00', end: '22:00' }]
      },
      {
        day: 'thursday',
        enabled: true,
        shifts: [{ start: '10:00', end: '22:00' }]
      },
      {
        day: 'friday',
        enabled: false,
        shifts: []
      },
      {
        day: 'saturday',
        enabled: true,
        shifts: [{ start: '10:00', end: '01:00' }]
      }
    ];
  }

  private cloneSchedule(schedule: DaySchedule[]): DaySchedule[] {
    return schedule.map((item) => ({
      ...item,
      shifts: item.shifts.map((shift) => ({ ...shift }))
    }));
  }
}
